import React from "react";
import { FormControl, Autocomplete, TextField, Skeleton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useListPatients } from "../../../store";
import PatientChip from "../../chips/patient-chip";

interface PatientOption {
  patientId: string;
  displayId: string;
}

interface PatientSelectProps {
  value?: string;
  patients?: PatientOption[];
  onChange: (selected: string) => void;
  disabled?: boolean;
  size?: "small" | "medium";
}

/**
 * Displays a patient selector using either the provided patient list or the
 * shared patient store. When controlled, the selected patient id is managed by
 * the parent component; otherwise the component behaves as an uncontrolled
 * select with the store-driven state.
 */
const PatientSelect: React.FC<PatientSelectProps> = ({
  value,
  patients: providedPatients,
  onChange,
  disabled,
  size = "medium",
}: PatientSelectProps) => {
  const theme = useTheme();
  const { data: loadedPatients = [], isLoading } = useListPatients();
  const patients = providedPatients ?? loadedPatients;
  const isControlled = typeof value !== "undefined";

  // Build a lookup map and sorted options once per patient list change.
  const { patientDictionary, patientIdList } = React.useMemo(() => {
    const patientDictionary: Record<string, string> = {};
    const patientIdList = patients
      .filter((patient): patient is PatientOption => Boolean(patient))
      .map((patient) => patient.patientId);

    patientIdList.forEach((patientId) => {
      const patient = patients.find(
        (candidate) => candidate?.patientId === patientId,
      );
      if (patient) {
        patientDictionary[patientId] = patient.displayId;
      }
    });

    patientIdList.sort((firstId, secondId) => {
      const firstDisplay = patientDictionary[firstId] ?? firstId;
      const secondDisplay = patientDictionary[secondId] ?? secondId;
      return firstDisplay.localeCompare(secondDisplay);
    });

    return { patientDictionary, patientIdList };
  }, [patients]);

  // Release the selected patient id as an empty string when the user clears the value.
  const handleAutocompleteChange = (
    _: React.SyntheticEvent,
    selectedValue: string | null,
  ) => {
    onChange(selectedValue ?? "");
  };

  if (isLoading) {
    return <Skeleton variant="rounded" width="100%" height="50px" />;
  }

  return (
    <FormControl fullWidth>
      <Autocomplete
        size={size}
        options={patientIdList}
        id="patient-select"
        disabled={disabled}
        multiple={false}
        value={isControlled ? (value ?? null) : undefined}
        onChange={handleAutocompleteChange}
        isOptionEqualToValue={(option, selected) => option === selected}
        getOptionLabel={(option) => patientDictionary[option] ?? ""}
        renderOption={(props, option) => (
          <li
            {...props}
            style={{
              ...props.style,
              display: "flex",
              width: "fit-content",
              maxWidth: "none",
              padding: 0,
            }}
          >
            <PatientChip
              label={patientDictionary[option] ?? option}
              size="medium"
              showTooltip={false}
            />
          </li>
        )}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              border: "1px solid #dbeafe",
              boxShadow: "0 12px 28px rgba(15, 23, 42, 0.12)",
              marginTop: 0.5,
            },
          },
          listbox: {
            sx: {
              display: "flex",
              flexWrap: "wrap",
              alignItems: "flex-start",
              columnGap: .4,
              rowGap: 0.5,
              padding: 0.5,
              borderRadius: "1rem",
              overflowX: "visible",
              "& .MuiAutocomplete-option": {
                margin: 0,
              borderRadius: "1rem",
                fontSize: "0.85rem",
                color: "#0f172a",
                minHeight: 0,
                justifyContent: "flex-start",
                padding: 0,
                width: "fit-content",
                maxWidth: "none",
                backgroundColor: "transparent",
                "& .MuiChip-root": {
                  transition: "opacity 140ms ease, filter 140ms ease",
                  border: `1px solid ${theme.palette.patientChip.background}`,
                },
                "&[aria-selected='true']": {
                  backgroundColor: "transparent",
                },
                "&.Mui-focused": {
                  backgroundColor: "transparent",
                },
                "&:hover .MuiChip-root, &.Mui-focused .MuiChip-root": {
                  border: `1px solid ${theme.palette.patientChip.main}`,
                },
              },
            },
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Patient"
            placeholder="Select patient"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
              "& .MuiInputBase-input": {
                fontSize: "0.85rem",
                color: "#0f172a",
              },
            }}
          />
        )}
        sx={{
          width: "100%",
          maxWidth: "100%",
        }}
      />
    </FormControl>
  );
};

export default PatientSelect;
